//react
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaArrowsRotate, FaFileInvoice, FaListCheck } from "react-icons/fa6";
import { useLocation, useNavigate } from "react-router-dom";

//styles
import "../invoice-bill/invoice-bill.style.scss";
import "./error-fix.stype.scss";
//components
import EmptyRecord from "../../components/empty-record/empty-record.component";
import FormError from "../../components/form-error/form-error.component";
import InlineNotice from "../../components/inline-notice/inline-notice.component";
import StructuredSideModal from "../../components/structured-side-modal/structured-side-modal.component";
import BillSummaryCard from "../../components/bill-summary-card/bill-summary-card.component";

//helpers
import { fromAdToBs, isEmptyObject } from "../../global/function";
import moment from "moment";
import Swal from "sweetalert2";
import { useQueryClient } from "@tanstack/react-query";
import { useCompanyDetails } from "../../queries/company.queries";
import {
	billDatesQueryKey,
	deriveFailedBillLists,
	useFailedBills,
	useRetryIRDBill,
	RetryIRDBillError,
} from "../../queries/bill.queries";
import type { Bill } from "../../queries/bill.queries";
import { useIrdStatus } from "../../hooks/useIrdStatus";
import { useSession } from "../../queries/auth.queries";
import { handleActivationKey } from "../../global/keyboardActivation";

interface ViewingBills {
	user: Bill[];
	all: Bill[];
}

export default function ErrorFixer() {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { state } = useLocation();
	const billData = state?.billData;

	const { data: companyData } = useCompanyDetails();
	const companyDetails = companyData?.company;
	const { data: session } = useSession();
	const { irdStatus, irdStatusLoading: isServerLoading, checkStatus } =
		useIrdStatus();
	const {
		data: failedBills,
		isLoading: isBillsLoading,
		isError: isFailedBillsError,
	} = useFailedBills();
	const retryMutation = useRetryIRDBill();

	const viewingBills: ViewingBills = useMemo(
		() => deriveFailedBillLists(failedBills, session?.company_user_id),
		[failedBills, session?.company_user_id],
	);
	const errBillsCount = useMemo(
		() => ({ user: viewingBills.user.length, all: viewingBills.all.length }),
		[viewingBills],
	);

	const [viewing, setViewing] = useState<"user" | "all">("user");
	const [selectedBill, setSelectedBill] = useState<Partial<Bill>>({});
	const [successNotice, setSuccessNotice] = useState<string | null>(null);
	// Mirrors invoice-bill.page.tsx's narrow-screen modal fallback — below
	// 1190px the right-hand detail panel is CSS-hidden (error-fix.stype.scss),
	// so selecting a bill there needs to open this instead.
	const [tempViewModal, setTempViewModal] = useState(false);

	// function to remove the state from location history of react router dom
	const clearState = useCallback(() => {
		navigate(".", { replace: true, state: {} });
	}, [navigate]);

	// One-time prefill from location.state (arriving from a bill-list "fix
	// this bill" link) once the failed-bills query has resolved at least once.
	const prefillDone = useRef(false);
	useEffect(() => {
		if (!failedBills || prefillDone.current) return;
		prefillDone.current = true;

		if (billData) {
			const parsedData = JSON.parse(billData);
			const userId = session?.company_user_id;
			setViewing(parsedData.company_user_id === userId ? "user" : "all");
			setSelectedBill({ ...parsedData });
			clearState();
		}
	}, [failedBills, billData, session?.company_user_id, clearState]);

	// Failed to load the unsynced-bills list at all — route through the same
	// persistent ERROR_MAP card /error already shows for bill/IRD failures,
	// instead of a toast that fires the instant before this page unmounts.
	useEffect(() => {
		if (isFailedBillsError) {
			navigate("/error", { state: { code: "error-fix-load-failed" } });
		}
	}, [isFailedBillsError, navigate]);

	// Every previously-unsynced bill is now synced — the destination (bill
	// list, failed-bill badge/count) already shows the result, so no separate
	// notification is needed here.
	useEffect(() => {
		if (failedBills && failedBills.length === 0) {
			queryClient.invalidateQueries({ queryKey: billDatesQueryKey });
			navigate("/invoice/bills");
		}
	}, [failedBills, navigate, queryClient]);

	async function handleCheckIRDStatus() {
		if (isServerLoading || companyDetails?.company_data_corrupt) return;
		//User can check if IRD servers are online
		await checkStatus();
	}

	// Function to retry bill
	async function handleRetrySync(promptedRetryBill: Partial<Bill> | null = null) {
		/*
		promptedRetryBill : is bill{},
		Bill was tried and returned duplicate
		Prior bill is canceled
		A new bill is created with bill ID with values from bill.
		*/
		if (retryMutation.isPending || companyDetails?.company_data_corrupt) return;

		const billToSend = promptedRetryBill ?? selectedBill;
		if (!billToSend?.bill_id) return;

		try {
			await retryMutation.mutateAsync({ bill_id: billToSend.bill_id });
			setSelectedBill({});
			setTempViewModal(false);
			setSuccessNotice("Bill is synced successfully");
		} catch (error) {
			if (error instanceof RetryIRDBillError && error.code === 91) {
				Swal.fire({
					title: "Duplicate Bill Detected?",
					text: `Let's retry syncing with a ID:${error.result?.invoice_number}`,
					showDenyButton: true,
					confirmButtonText: "Retry",
					denyButtonText: "Cancel",
					customClass: {
						popup: "alert-modal",
						actions: "btn-container",
						confirmButton: "alert-btn btn-confirm",
						denyButton: "alert-btn btn-cancel",
					},
				}).then((click) => {
					if (click.isConfirmed) {
						//recurssive retrying same fuction from user prompt action.
						handleRetrySync(error.result ?? null);
					} else {
						const fallbackBill = [...viewingBills.user, ...viewingBills.all].find(
							(bill) => bill.bill_id === error.result?.bill_id,
						);
						setSelectedBill(fallbackBill ?? {});
					}
				});
			}
		}
	}

	function handleOnSelectBill(bill: Bill) {
		if (bill?.ird_bill !== "success") {
			setSelectedBill(bill);
			if (window.innerWidth <= 1190) {
				setTempViewModal(true);
			}
		}
	}

	function handleOnChangeViewer(viewType: "user" | "all") {
		setViewing(viewType);
		setSelectedBill({});
		setTempViewModal(false);
	}

	function handleModalClose() {
		setTempViewModal(false);
		if (window.innerWidth <= 1190) {
			setSelectedBill({});
		}
	}

	function _renderBillDate(date?: string) {
		if (!date) return "";
		const billDate = `${fromAdToBs(date, "DD MMMM, YYYY")} | ${moment(
			date,
		).format("DD MMMM, YYYY")}`;
		return billDate;
	}

	// Short, single-line date for the dense list row — the full BS+AD pair
	// (_renderBillDate) is reserved for the detail panel, where there's room.
	function _renderShortDate(date?: string) {
		if (!date) return "";
		return moment(date).format("DD MMM, YYYY");
	}

	// Relative "failed X ago" — replaces the old binary "New!!" flag with a
	// real staleness signal (the thing an ops user triaging this queue
	// actually wants to prioritize by), while still calling out very-recent
	// failures distinctly.
	function _renderBillAge(bill: Bill) {
		if (!bill.updatedAt) return null;
		const isRecent =
			Date.now() - new Date(bill.updatedAt).getTime() < 3600000; // 1 hour
		return (
			<span
				className={`err-age-badge ${isRecent ? "is-recent" : ""}`}
			>
				{isRecent ? "New" : moment(bill.updatedAt).fromNow()}
			</span>
		);
	}

	// Every bill shown on this page is, by definition, unsynced with IRD — no
	// canceled/credit-issued branching to consider like invoice-bill.page.tsx's
	// equivalent buildSummaryProps, so irdBillStatus (pending or failed) is
	// the only badge that ever shows here, alongside billMeta's independent
	// external-app sync badge when this bill is also connected to one.
	function buildSummaryProps(bill: Partial<Bill>) {
		return {
			documentLabel: `Invoice #${bill.invoice_number}`,
			date: bill.invoice_date,
			dateNepali: bill.invoice_date_nepali,
			salesDate: bill.sales_date,
			buyerName: bill.buyer_name ?? "",
			buyerPan: bill.buyer_pan,
			customerMobile: bill.customer_mobile ?? bill.customer_info?.customer_mobile,
			paymentMode: bill.payment_mode,
			// Includes the user ID (not just name) — useful triage context this
			// page's old hand-rolled "Associate Name" block used to show that
			// BillSummaryCard's generic recordedBy line doesn't include by default.
			recordedBy: bill.company_user?.user
				? `${bill.company_user.user.first_name ?? ""} ${bill.company_user.user.last_name ?? ""} (ID: ${bill.company_user.user.user_id ?? "—"})`
				: undefined,
			printCopies: bill.print_copies,
			lastPrintDate: bill.lastPrintDate,
			billedItems: bill.billed_items ?? [],
			subTotal: bill.sub_total ?? 0,
			totalSales: bill.total_sales ?? 0,
			discount: (bill.non_taxable_discount ?? 0) + (bill.taxable_discount ?? 0),
			serviceCharge: (bill.non_taxable_service_charge ?? 0) + (bill.taxable_service_charge ?? 0),
			nonTaxableAmount: bill.non_taxable_amt,
			taxableAmount: bill.esf ? bill.amount_for_esf : bill.taxable_sales_vat,
			vat: bill.vat,
			esf: bill.esf,
			irdBillStatus: bill.ird_bill,
			billMeta: bill.bill_meta,
		};
	}

	function _renderFailedBillList() {
		if (viewingBills[viewing].length === 0) return;

		return (
			<div className="err-bill-list">
				<div className="scroll-panel-wrapper err-bill-list-body">
					{viewingBills[viewing].map((bill, i) => (
						<div
							key={i}
							role="button"
							tabIndex={0}
							aria-pressed={selectedBill.bill_id === bill.bill_id}
							className={`err-bill-row ${
								selectedBill.bill_id === bill.bill_id
									? "selected-bill"
									: ""
							}`}
							onClick={() => handleOnSelectBill(bill)}
							onKeyDown={(e) =>
								handleActivationKey(e, () =>
									handleOnSelectBill(bill),
								)
							}
						>
							{bill.ird_bill === "success" && (
								<div className="err-corrected-blur">SYNCED</div>
							)}
							<div className="err-row-top">
								<span className="err-row-invoice">
									{bill.invoice_number}
								</span>
								<span className="err-row-status">
									{_renderBillAge(bill)}
								</span>
							</div>
							<div className="err-row-meta">
								<span
									title={_renderBillDate(bill.invoice_date)}
								>
									{_renderShortDate(bill.invoice_date)}
								</span>
								<span className="err-row-dot">&middot;</span>
								<span>
									{bill.company_user?.user?.first_name}{" "}
									{bill.company_user?.user?.last_name}
								</span>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}
	// The instructional copy needs to name whichever button is actually
	// rendered below it — it used to hardcode "Retry Sync", a label that
	// appears on neither the "Sync IRD" nor "Check IRD Status" button.
	const retryActionLabel = irdStatus ? "Sync IRD" : "Check IRD Status";

	// Shared between the inline right-panel (wide screens) and the SideModal
	// (<=1190px) — same header copy/button either way, so the two surfaces
	// can't drift apart.
	function renderBillDetailHeader() {
		const hasBill = !isEmptyObject(selectedBill);
		return (
			<div className="header-block">
				<div className="err-description-wrapper">
					<div className="title">
						<FaFileInvoice />
						Bill Details
					</div>
					<div className="description">
						{hasBill ? (
							<>
								This bill is not synced with IRD servers.
								Please click the <b>{retryActionLabel}</b>{" "}
								button
							</>
						) : (
							"Select a bill from the list to view its details and retry syncing with IRD."
						)}
					</div>
				</div>
				{hasBill &&
					(irdStatus ? (
						<button
							className="retry-button"
							onClick={() => handleRetrySync()}
							disabled={
								retryMutation.isPending ||
								companyDetails?.company_data_corrupt
							}
						>
							<FaArrowsRotate />
							{retryMutation.isPending
								? "Syncing..."
								: "Sync IRD"}
						</button>
					) : (
						<button
							className="retry-button"
							onClick={() => handleCheckIRDStatus()}
							disabled={
								isServerLoading ||
								companyDetails?.company_data_corrupt
							}
						>
							<FaArrowsRotate />
							{isServerLoading
								? "Checking..."
								: "Check IRD Status"}
						</button>
					))}
			</div>
		);
	}

	// Reuses BillSummaryCard (the same canonical preview invoice-bill.page.tsx
	// uses) instead of a second hand-rolled items/totals table that had drifted
	// out of sync with it — see buildSummaryProps above.
	function renderBillDetailBody() {
		if (isEmptyObject(selectedBill)) {
			return (
				<div className="default-blank">
					{isBillsLoading ? null : (
						<EmptyRecord caption="Select Bill To Verify" />
					)}
				</div>
			);
		}
		return (
			<>
				<BillSummaryCard {...buildSummaryProps(selectedBill)} />
				{selectedBill.sale_info && (
					<div className="err-invoice-card">
						<div className="err-note">
							<div className="err-content-block">
								<div className="label">Bill Notes</div>
								<div className="data">
									{selectedBill.sale_info}
								</div>
							</div>
						</div>
					</div>
				)}
			</>
		);
	}

	return (
		<div className="err-page-wrapper">
			<div className="err-body-wrapper">
				<div className="panel error-bill-list-wrapper left-panel">
					<div className="panel-header">
						<div className="header-block">
							<div className="err-description-wrapper">
								<div className="title">
									<FaListCheck />
									Bill List
								</div>
								<div className="description">
									<b>
										{errBillsCount.user + errBillsCount.all}
									</b>{" "}
									Bills in this list have not synced with IRD
									servers
								</div>
							</div>
						</div>
					</div>
					<div className="err-filter-container" role="tablist">
						<div
							className={`err-fetch-filter ${
								viewing === "user" ? "active" : ""
							}`}
							role="tab"
							aria-selected={viewing === "user"}
							tabIndex={viewing === "user" ? -1 : 0}
							onClick={() => handleOnChangeViewer("user")}
							onKeyDown={(e) =>
								handleActivationKey(e, () =>
									handleOnChangeViewer("user"),
								)
							}
						>
							My Bills
							<b>{errBillsCount.user}</b>
						</div>
						<div
							className={`err-fetch-filter ${
								viewing === "all" ? "active" : ""
							}`}
							role="tab"
							aria-selected={viewing === "all"}
							tabIndex={viewing === "all" ? -1 : 0}
							onClick={() => handleOnChangeViewer("all")}
							onKeyDown={(e) =>
								handleActivationKey(e, () =>
									handleOnChangeViewer("all"),
								)
							}
						>
							Team Bills
							<b>{errBillsCount.all}</b>
						</div>
					</div>
					{_renderFailedBillList()}
				</div>
				<div className="panel err-bill-display right-panel">
					<div className="panel-header">
						{renderBillDetailHeader()}
						<FormError
							isError={retryMutation.isError}
							error={retryMutation.error}
							fallback="Failed to sync bill. Please try again."
						/>
						<InlineNotice
							message={successNotice}
							type="success"
							autoHideMs={3000}
							onDismiss={() => setSuccessNotice(null)}
						/>
					</div>
					<div className="scroll-panel-wrapper">
						{renderBillDetailBody()}
					</div>
				</div>
			</div>
			<StructuredSideModal
				open={tempViewModal}
				onOpenChange={(open) => { if (!open) handleModalClose(); }}
				title="Bill Details"
				width={560}
			>
				<div style={{ padding: "16px" }}>
					{renderBillDetailHeader()}
					<FormError
						isError={retryMutation.isError}
						error={retryMutation.error}
						fallback="Failed to sync bill. Please try again."
					/>
					<InlineNotice
						message={successNotice}
						type="success"
						autoHideMs={3000}
						onDismiss={() => setSuccessNotice(null)}
					/>
					<div className="scroll-panel-wrapper">
						{renderBillDetailBody()}
					</div>
				</div>
			</StructuredSideModal>
		</div>
	);
}
